import { isEmpty } from 'lodash';

const wordSegmenter = new Intl.Segmenter(undefined, { granularity: 'word' });

/**
 * A text field's tokens as ES's `standard` analyzer emits them, which is what core-api indexes text
 * components and the omni search with: words split on Unicode word boundaries, then lowercased.
 * Accents are kept.
 */
export function getStandardTokens(text: string): string[] {
  return Array.from(wordSegmenter.segment(text))
    .filter(segment => segment.isWordLike)
    .map(segment => segment.segment.toLowerCase());
}

/**
 * A scalar text field's tokens as core-api's `scalar_text_analyzer` emits them (Email, File names,
 * URL): runs of letters only - digits and punctuation split and are dropped - lowercased, with
 * accents folded.
 */
export function getLetterTokens(text: string): string[] {
  return (text.match(/\p{L}+/gu) ?? []).map(token => foldAccents(token.toLowerCase()));
}

/**
 * ES `asciifolding`, as far as Unicode decomposition reaches: accented letters lose their accents.
 * Letters it doesn't decompose (ø, ß, æ) are left as they are.
 */
export function foldAccents(text: string): string {
  return text.normalize('NFD').replace(/\p{M}/gu, '');
}

/**
 * ES `match_phrase_prefix`, which core-api runs for text filters and the omni search: the query's
 * tokens appear in order and next to each other, every one whole except the last, which only has
 * to start a token. A query with no tokens matches nothing.
 */
export function matchesPhrasePrefix(valueTokens: string[], queryTokens: string[]): boolean {
  if (isEmpty(queryTokens)) {
    return false;
  }
  const lastOffset = queryTokens.length - 1;
  for (let start = 0; start + lastOffset < valueTokens.length; start++) {
    const isMatch = queryTokens.every((queryToken, offset) => {
      const valueToken = valueTokens[start + offset];
      return offset === lastOffset ? valueToken.startsWith(queryToken) : valueToken === queryToken;
    });
    if (isMatch) {
      return true;
    }
  }
  return false;
}
